import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { PaymentElement, useStripe, useElements, Elements } from '@stripe/react-stripe-js'
import { loadStripe } from '@stripe/stripe-js'
import AdminLayout from '@/components/layout/AdminLayout'
import Button from '@/components/ui/Button'
import { STRIPE_CONFIG } from '@/config/stripe'

const API_BASE_URL = import.meta.env.VITE_API_URL || ''
const stripePromise = loadStripe(STRIPE_CONFIG.publishableKey)

function PaymentForm({ onCancel }: { onCancel: () => void }) {
  const stripe = useStripe()
  const elements = useElements()
  const [paymentProcessing, setPaymentProcessing] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!stripe || !elements) {
      setError('Stripe has not loaded yet')
      return
    }

    setPaymentProcessing(true)
    setError('')
    setSuccess('')

    try {
      const { error: submitError } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/admin/payment-test`,
        },
        redirect: 'if_required',
      })

      if (submitError) {
        setError(submitError.message || 'Payment failed')
      } else {
        setSuccess('✅ Payment successful! Check your Stripe dashboard.')
        setTimeout(onCancel, 2000) // Reset form after 2 seconds
      }
    } catch (err) {
      console.error('Payment error:', err)
      setError(err instanceof Error ? err.message : 'Payment failed')
    } finally {
      setPaymentProcessing(false)
    }
  }

  return (
    <>
      {/* Error Message */}
      {error && (
        <div className="card shimmer" style={{
          padding: 'var(--spacing-lg)',
          marginBottom: 'var(--spacing-lg)',
          background: 'linear-gradient(135deg, rgba(225, 29, 72, 0.1) 0%, rgba(225, 29, 72, 0.05) 100%)',
          border: '1px solid rgba(225, 29, 72, 0.2)',
        }}>
          <div className="flex items-center" style={{ gap: 'var(--spacing-md)' }}>
            <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{
              background: 'var(--accent-danger)',
              color: 'white'
            }}>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <p className="font-medium" style={{ color: 'var(--accent-danger)' }}>{error}</p>
          </div>
        </div>
      )}

      {/* Success Message */}
      {success && (
        <div className="card shimmer" style={{
          padding: 'var(--spacing-lg)',
          marginBottom: 'var(--spacing-lg)',
          background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(16, 185, 129, 0.05) 100%)',
          border: '1px solid rgba(16, 185, 129, 0.2)',
        }}>
          <div className="flex items-center" style={{ gap: 'var(--spacing-md)' }}>
            <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{
              background: 'var(--accent-success)',
              color: 'white'
            }}>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <p className="font-medium" style={{ color: 'var(--accent-success)' }}>{success}</p>
          </div>
        </div>
      )}

      {/* Payment Form */}
      <form onSubmit={handleSubmit} className="card" style={{ padding: 'var(--spacing-xl)' }}>
        <h3 className="text-lg font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
          Payment Details
        </h3>

        <div style={{ marginBottom: 'var(--spacing-lg)' }}>
          <PaymentElement />
        </div>

        <div className="flex gap-4">
          <Button
            type="submit"
            disabled={!stripe || paymentProcessing}
            className="flex-1"
          >
            {paymentProcessing ? 'Processing...' : 'Pay Now'}
          </Button>

          <Button
            type="button"
            onClick={onCancel}
            className="flex-1"
            style={{ background: 'var(--background-secondary)' }}
          >
            Cancel
          </Button>
        </div>
      </form>
    </>
  )
}

function PaymentTestForm() {
  const [amount, setAmount] = useState('10.00')
  const [clientSecret, setClientSecret] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  const createPaymentIntent = async () => {
    setLoading(true)
    setError('')
    setSuccess('')
    setClientSecret(null)

    try {
      const token = localStorage.getItem('authToken')
      const amountInCents = Math.round(parseFloat(amount) * 100)

      const response = await fetch(`${API_BASE_URL}/api/v1/admin/payments/test/create-intent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          amount: amountInCents,
          currency: 'usd',
          description: 'Test payment from admin panel',
        }),
      })

      if (!response.ok) {
        const errorData = await response.json()
        // Handle ProblemDetail format from Spring Boot
        const errorMessage = errorData.detail || errorData.message || errorData.title || 'Failed to create payment intent'
        throw new Error(errorMessage)
      }

      const data = await response.json()
      setClientSecret(data.clientSecret)
      setSuccess('Payment form ready! Fill in your card details below.')
    } catch (err) {
      console.error('Payment intent creation error:', err)
      setError(err instanceof Error ? err.message : 'Failed to create payment intent')
    } finally {
      setLoading(false)
    }
  }

  const handleCancelPayment = () => {
    setClientSecret(null)
    setError('')
    setSuccess('')
    setAmount('10.00')
  }

  return (
    <div className="max-w-2xl mx-auto" style={{ padding: 'var(--spacing-2xl)' }}>
      {/* Header */}
      <div className="card shimmer" style={{ padding: 'var(--spacing-2xl)', marginBottom: 'var(--spacing-xl)' }}>
        <div className="text-center">
          <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto" style={{
            background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
            marginBottom: 'var(--spacing-lg)',
            boxShadow: '0 4px 20px rgba(16, 185, 129, 0.3)'
          }}>
            <svg className="w-10 h-10" style={{ color: 'white' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
            </svg>
          </div>
          <h1 className="text-3xl font-bold gradient-text mb-2">Payment Test</h1>
          <p style={{ color: 'var(--text-secondary)' }}>
            Test Stripe payment integration
          </p>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="card shimmer" style={{
          padding: 'var(--spacing-lg)',
          marginBottom: 'var(--spacing-lg)',
          background: 'linear-gradient(135deg, rgba(225, 29, 72, 0.1) 0%, rgba(225, 29, 72, 0.05) 100%)',
          border: '1px solid rgba(225, 29, 72, 0.2)',
        }}>
          <div className="flex items-center" style={{ gap: 'var(--spacing-md)' }}>
            <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{
              background: 'var(--accent-danger)',
              color: 'white'
            }}>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <p className="font-medium" style={{ color: 'var(--accent-danger)' }}>{error}</p>
          </div>
        </div>
      )}

      {/* Success Message */}
      {success && (
        <div className="card shimmer" style={{
          padding: 'var(--spacing-lg)',
          marginBottom: 'var(--spacing-lg)',
          background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(16, 185, 129, 0.05) 100%)',
          border: '1px solid rgba(16, 185, 129, 0.2)',
        }}>
          <div className="flex items-center" style={{ gap: 'var(--spacing-md)' }}>
            <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{
              background: 'var(--accent-success)',
              color: 'white'
            }}>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <p className="font-medium" style={{ color: 'var(--accent-success)' }}>{success}</p>
          </div>
        </div>
      )}

      {/* Amount Input */}
      {!clientSecret && (
        <div className="card" style={{ padding: 'var(--spacing-xl)', marginBottom: 'var(--spacing-lg)' }}>
          <label className="block mb-4">
            <span className="text-sm font-medium" style={{ color: 'var(--text-primary)', marginBottom: 'var(--spacing-sm)', display: 'block' }}>
              Test Amount (USD)
            </span>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-secondary)' }}>$</span>
              <input
                type="number"
                step="0.01"
                min="0.50"
                max="999999.99"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="w-full pl-8 pr-4 py-3 border rounded-lg"
                style={{
                  borderColor: 'var(--border-primary)',
                  backgroundColor: 'var(--background-primary)',
                  color: 'var(--text-primary)',
                }}
                placeholder="10.00"
              />
            </div>
            <p className="text-xs mt-2" style={{ color: 'var(--text-muted)' }}>
              Use test cards: 4242 4242 4242 4242 (success) or 4000 0000 0000 9995 (decline)
            </p>
          </label>

          <Button
            onClick={createPaymentIntent}
            disabled={loading || !amount || parseFloat(amount) < 0.5}
            className="w-full"
          >
            {loading ? 'Creating...' : 'Create Payment Form'}
          </Button>
        </div>
      )}

      {/* Payment Form */}
      {clientSecret && (
        <Elements
          stripe={stripePromise}
          options={{
            clientSecret,
            appearance: {
              theme: 'stripe',
              variables: {
                colorPrimary: '#0B4D2A',
                fontFamily: 'system-ui, sans-serif',
                borderRadius: '8px',
              },
            },
          }}
        >
          <PaymentForm onCancel={handleCancelPayment} />
        </Elements>
      )}

      {/* Test Card Info */}
      <div className="card" style={{ padding: 'var(--spacing-lg)', marginTop: 'var(--spacing-lg)' }}>
        <h4 className="font-bold mb-3" style={{ color: 'var(--text-primary)' }}>Test Card Numbers</h4>
        <div className="space-y-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
          <div className="flex justify-between">
            <span>✅ Success:</span>
            <code className="font-mono">4242 4242 4242 4242</code>
          </div>
          <div className="flex justify-between">
            <span>❌ Declined:</span>
            <code className="font-mono">4000 0000 0000 9995</code>
          </div>
          <div className="flex justify-between">
            <span>⚠️ Requires Auth:</span>
            <code className="font-mono">4000 0025 0000 3155</code>
          </div>
          <p className="text-xs mt-3" style={{ color: 'var(--text-muted)' }}>
            Use any future expiry date, any 3-digit CVC, and any ZIP code
          </p>
        </div>
      </div>
    </div>
  )
}

export default function PaymentTestPage() {
  const navigate = useNavigate()

  const handleLogout = () => {
    localStorage.removeItem('authToken')
    localStorage.removeItem('refreshToken')
    localStorage.removeItem('user')
    navigate('/admin/login')
  }

  return (
    <AdminLayout onLogout={handleLogout}>
      <PaymentTestForm />
    </AdminLayout>
  )
}
